import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { combineLatest } from 'rxjs';
import { filter, switchMap, take, tap } from 'rxjs/operators';
import { Order } from 'src/app/model/interfaces/order';
import { trackPageView, trackEvent, trackProduct, trackFlow, TrackingProduct, PromotionProps, PageViewFlow } from './tealium.utils';
import { ShareType } from 'src/app/model/interfaces/share-type';
import { TrackingChannelData } from 'src/app/model/TrackingChannelData';
import { PriceService } from '../price.service';
import { StorageService } from '../storage.service';
import { MembershipState } from '../../model/enums/membership-state';
import { PaymentTypeLabel } from '../../model/enums/payment-type-label';

@Injectable({ providedIn: 'root' })
export class TrackingFacadeService
{
    public dataLayer: any;

    private readonly router: Router;
    private readonly storageService: StorageService;
    private readonly priceService: PriceService;

    public constructor(
        router: Router,
        storageService: StorageService,
        priceService: PriceService
    )
    {
        this.router = router;
        this.storageService = storageService;
        this.priceService = priceService;
        if (window['dataLayer'] != null)
          {
              this.dataLayer = window['dataLayer'];
          }

        //this.deteckTrackingchannelData();
        this.initializeEventListeners();
    }


    private getUrlParameter(name) 
    {
        name = name.replace(/\\/g, '\\\\').replace(/\[/g, '\\[').replace(/\]/g, '\\]');
        var regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
        var results = regex.exec(location.search);
        return results === null ? null : decodeURIComponent(results[1].replace(/\+/g, ' '));
    }

    private deteckTrackingchannelData(): TrackingChannelData 
    {
        const gclid = this.getUrlParameter('gclid') || null;
        const msclkid = this.getUrlParameter('msclkid') || null;
        const fbclid = this.getUrlParameter('fbclid') || null;
        const snapid = this.getUrlParameter('snapid') || null;
        const source = this.getUrlParameter('utm_source') || null;
        const medium = this.getUrlParameter('utm_medium') || null;
        const coid = this.getUrlParameter('coid') || null;
        const externalCompetitionReferrer = this.getUrlParameter('lfreferrer') || null;
        const externalCompetitionTrackingChannel = this.getUrlParameter('lftrackingchannel') || null;

        var trackingChannelParams: TrackingChannelData = {
            'referrer': externalCompetitionReferrer === null ? document.referrer: externalCompetitionReferrer,
			      'fbclid': fbclid,
			      'snapid': snapid,
            'gclid': gclid,
            'msclkid': msclkid,
            'source': source,
            'medium': medium,
            'coid': coid,
            'lftrackingchannel': externalCompetitionTrackingChannel,
            'lfreferrer': externalCompetitionReferrer
        };

        var trackingChannelObject = JSON.parse(sessionStorage.getItem('trackingChannel')) as TrackingChannelData;
        if (!trackingChannelObject) {
            sessionStorage.setItem('trackingChannel', JSON.stringify(trackingChannelParams));
            return trackingChannelParams;
        }
        else {
            return trackingChannelObject;
        }
    }

    private resolveSubscriptionVariant(classCount: number): PaymentTypeLabel {
      return classCount !== 1
        ? PaymentTypeLabel.SixMonthBetalingsservice
        : PaymentTypeLabel.OneMonthBetalingsservice;
    }

    public getCoid(): number
    {
        const valueFromSessionStorage = sessionStorage.getItem('trackingChannel') ;
        const convertedSessionStorageValue = valueFromSessionStorage === null ? {} : JSON.parse(valueFromSessionStorage);

        let coid = 1;

        if(convertedSessionStorageValue.coid ){
            coid= parseInt(convertedSessionStorageValue.coid, 10);
        }

        return coid;
    }

    private initializeEventListeners():void
    {
        // setup router.navigationEnd subscription for pageviews events
        this.router.events.pipe(
        filter((event: any) => event instanceof NavigationEnd))
        .subscribe((event =>
        {
            // do pageview on every change
            const pathString = event.url;
            const pathWithoutQueryParameter = pathString.split("?")[0];
            const lastpart = pathWithoutQueryParameter.split("/").pop();

            // Pages that are a checkout-flow step carry their flow_info on the page_view itself
            // (a single combined view) rather than as a separate flow event.
            this.virtualPageView(pathWithoutQueryParameter, lastpart, this.getPageViewFlow(pathWithoutQueryParameter));

            // TODO
            // on every page  track userInfo


            // do initCheckout
            /*
            if(event.url === '/betaling/om-dig'){
                this.startCheckout();
            }
            */
        }));

    }

  /**
   * Tracks virtual page views for SPA navigation by pushing to Google Tag Manager dataLayer utag.
   * @param path - The virtual path of the page (URL without query parameters)
   * @param route - The route name (last segment of the path)
   * @param flow - Optional checkout-flow step; when set, the page view carries `flow_info`
   */
  public virtualPageView(path: string, route: string, flow?: PageViewFlow): void {
    // Ensighten
    if (this.dataLayer) {
      this.dataLayer.push({
        event: "virtualPage",
        page: {
          virtualPath: path,
          name: route
        }
      })
    }
    
    // Tealium
    const pageType = (window as any)['tealiumTrackData']?.()?.page_type ?? 'unknown';

    // Flow-step pages send a single combined view that carries `flow_info`. In the package a
    // `trackFlow({ type: 'view' })` is a `utag.view()` page_view enriched with flow_info — i.e. a
    // page view "with a bit more information" — so we use it instead of a separate flow event.
    if (flow) {
      trackFlow({ type: 'view', eventList: ['page_view'], flowInfo: flow });
      return;
    }

    trackPageView({ eventList: ['page_view'], pageType });
  }

  /**
   * Returns the checkout-flow step for pages that should carry `flow_info` on their page view. Per
   * tracking (Nicklas, 2026-06-23): `flow_info` describes the page, so it belongs on the single
   * `page_view` (sent via `trackFlow({ type: 'view' })`) — not as a separate flow/action event.
   */
  private getPageViewFlow(path: string): PageViewFlow | undefined {
    // Step 1 — payment method selection page.
    if (path.startsWith('/betaling/betalingsmetode')) {
      return { userFlow: 'Klasselotteriet', flowStep: '1', flowStepName: 'payment_method', flowType: 'normal' };
    }
    // Step 2 — game plan / period selection page.
    if (path.startsWith('/betaling/dit-spil')) {
      return { userFlow: 'Klasselotteriet', flowStep: '2', flowStepName: 'confirm_selection', flowType: 'normal' };
    }
    // Step 2 — Betalingsservice details page.
    if (path.startsWith('/betaling/betaling')) {
      return { userFlow: 'Klasselotteriet', flowStep: '2', flowStepName: 'bs_oplysninger', flowType: 'normal' };
    }
    // Step 3 — receipt/confirmation page (kvittering). Fired for every completed purchase regardless
    // of payment method.
    if (path.startsWith('/kvittering')) {
      return { userFlow: 'Klasselotteriet', flowStep: '3', flowStepName: 'receipt', flowType: 'normal' };
    }
    return undefined;
  }

  /**
   * Pushes tracking data to Google Tag Manager dataLayer utag.
   * @param data - The tracking event data object to push
   */
  private pushToDataLayer(data: any): void {
    if (this.dataLayer) {
      this.dataLayer.push(data);
    }
  }

  public addToCart(productArray: TrackingProduct[], cartTotalItems?: number, cartTotalRows?: number, cartTotalPrice?: number): void {
    // Ensighten
    this.pushToDataLayer({
      event: "addToCart",
      ecommerce: {
        add: {
          products: productArray
        }
      }
    });
    
    // Tealium trackProduct
    trackProduct({ 
      eventList: ['add_to_cart'], 
      products: productArray as TrackingProduct[],
      cart_total_items: cartTotalItems !== undefined ? String(cartTotalItems) : undefined,
      cart_total_rows: cartTotalRows !== undefined ? String(cartTotalRows) : undefined,
      cart_total: cartTotalPrice !== undefined ? String(cartTotalPrice) : undefined,
    });
  }

  public removeFromCart(productArray: TrackingProduct[], cartTotalItems?: number, cartTotalRows?: number, cartTotalPrice?: number): void {
    // Ensighten
    this.pushToDataLayer({
      event: "removeFromCart",
      ecommerce: {
        remove: {
          products: productArray
        }
      }
    });
    
    // Tealium trackProduct
    trackProduct({ 
      eventList: ['remove_from_cart'], 
      products: productArray as TrackingProduct[],
      cart_total_items: cartTotalItems !== undefined ? String(cartTotalItems) : undefined,
      cart_total_rows: cartTotalRows !== undefined ? String(cartTotalRows) : undefined,
      cart_total: cartTotalPrice !== undefined ? String(cartTotalPrice) : undefined,
    });
  }

  /**
   * Tracks a view of the basket (the /lodder-til-salg/kurv page) with all products currently in it.
   * @param productArray all products currently in the basket
   * @param cartTotalItems total number of basket items (shares + packages)
   * @param cartTotalRows total number of rows (individual ticket rows across shares and packages)
   * @param cartTotalPrice total basket price
   */
  public basketView(productArray: TrackingProduct[], cartTotalItems?: number, cartTotalRows?: number, cartTotalPrice?: number): void {
    // Ensighten
    this.pushToDataLayer({
      event: "viewCart",
      ecommerce: {
        products: productArray
      }
    });

    // Tealium trackProduct
    trackProduct({
      eventList: ['view_cart'],
      products: productArray as TrackingProduct[],
      cart_total_items: cartTotalItems !== undefined ? String(cartTotalItems) : undefined,
      cart_total_rows: cartTotalRows !== undefined ? String(cartTotalRows) : undefined,
      cart_total: cartTotalPrice !== undefined ? String(cartTotalPrice) : undefined,
    });
  }

  /**
   * Tracks a view of the payment method page (/betalingsmetode) with all products in the order.
   * @param productArray all products in the order
   * @param cartTotalItems total number of items (shares + packages)
   * @param cartTotalRows total number of rows (individual ticket rows across shares and packages)
   * @param cartTotalPrice total order price
   */
  public paymentMethodView(productArray: TrackingProduct[], cartTotalItems?: number, cartTotalRows?: number, cartTotalPrice?: number): void {
    // Ensighten
    this.pushToDataLayer({
      event: "checkout",
      ecommerce: {
        checkout: {
          actionField: {
            step: "betalingsmetode",
          },
          products: productArray
        }
      }
    });

    // Tealium trackProduct
    trackProduct({
      eventList: ['checkout'],
      products: productArray as TrackingProduct[],
      cart_total_items: cartTotalItems !== undefined ? String(cartTotalItems) : undefined,
      cart_total_rows: cartTotalRows !== undefined ? String(cartTotalRows) : undefined,
      cart_total: cartTotalPrice !== undefined ? String(cartTotalPrice) : undefined,
    });
  }

  public startCheckout(productArray: TrackingProduct[]): void {    // Ensighten
    this.pushToDataLayer({
      event: "checkout",
      ecommerce: {
        checkout: {
          actionField: {
            step: 1,
          },
          products: productArray
        }
      }
    });

    // Tealium: flow_info is sent on the page_view (see getPageViewFlow), not as a separate flow event.
  }

  public checkoutGamePlan(productArray: TrackingProduct[]): void {
    // Ensighten
    this.pushToDataLayer({
      event: "checkout",
      ecommerce: {
        checkout: {
          actionField: {
            step: 2,
          },
          products: productArray
        }
      }
    });

    // Tealium: flow_info is sent on the page_view (see getPageViewFlow), not as a separate flow event.
  }
  
  public checkoutGapFunding(productArray: TrackingProduct[]): void {
    // Ensighten
    this.pushToDataLayer({
      event: "checkout",
      ecommerce: {
        checkout: {
          actionField: {
            step: 3,
          },
          products: productArray
        }
      }
    });
    
    // Tealium trackEvent
    trackEvent({ 
      name: 'gap_funding', 
      type: 'link',
    });
  }
  
  public competitionSignup(competitionId: number): void {
    // Ensighten
    this.pushToDataLayer({
      event: "gaEvent",
      eventSyntax: {
        category: "signup_competition",
        action: "signup_succes",
        label: competitionId,
      }
    });
    
    // Tealium trackEvent
    trackEvent({ 
      name: 'competition_signup', 
      type: 'link', 
      category: String(competitionId),
    });
  }

  public newzletterSubscribe(subscribe: boolean): void {
    const categoryValue = subscribe ? "signup_newsletter" : "unsubscribe_newsletter";
    const actionValue = subscribe ? "signup_succes" : "unsubscribe_succes";

    // Ensighten
    this.pushToDataLayer({
      event: "gaEvent",
      eventSyntax: {
        category: categoryValue,
        action: actionValue,
        label: ""
      }
    });
    
    // Tealium trackEvent
    trackEvent({ 
      name: subscribe ? 'newsletter_subscribe' : 'newsletter_unsubscribe', 
      type: 'link',
    });
  }

    public trackUser(): void
    {
        // TODO make trackUser data contract (Bruger)
        // TODO send info to ensighten
    }

  public login(loginType: string): void {
    // Ensighten
    this.pushToDataLayer({
      event: 'gaEvent',
      eventSyntax: {
        category: 'login',
        action: 'login_succes',
        label: loginType
      }
    });

    // Tealium trackEvent
    trackEvent({ 
      name: 'login', 
      type: 'link', 
      category: loginType,
    });
  }

    ///////////////////////////////////////////////////
    ////////////////// NYT KØBSFLOW ///////////////////
    ///////////////////////////////////////////////////

  public howToPlayModalOpened(path: string): void {
    // Ensighten
    this.pushToDataLayer({
      event: 'gaEvent',
      eventSyntax: {
        category: 'klasselotteriet',
        action: 'lottery_details_popup_open',
        label: 'Sådan fungerer det',
        path: path,
      }
    });

    // Tealium modal event (modal_shown) is fired centrally in ModalService for every ngb-modal-window modal.
  }

  public shopOverviewNavigation(productType: string): void {
    // Ensighten
    this.pushToDataLayer({
      event: "gaEvent",
      eventSyntax: {
        action: "choose_ticket",
        category: "klasselotteriet",
        label: productType // Det der klikkes på (lodpakker, enkeltlodder, ønskelod)
      }
    });
    
    // Tealium trackEvent
    trackEvent({ 
      name: 'choose_ticket', 
      type: 'link', 
      category: productType,
    });
  }

  public shopTabNavigation(path: string): void {
    // Ensighten
    this.pushToDataLayer({
      event: 'gaEvent',
      eventSyntax: {
        category: 'klasselotteriet',
        action: 'ticket_tab_switch',
        label: path,
        path: path,
      }
    });
    
    // Tealium trackEvent
    trackEvent({ 
      name: 'ticket_tab_switch', 
      type: 'link', 
      url: path,
    });
  }

  public shopWinchanceOpened(path: string, label: string): void {
    // Ensighten
    this.pushToDataLayer({
      event: 'gaEvent',
      eventSyntax: {
        category: 'klasselotteriet',
        action: 'winner_details_popup_open',
        label: label,
        path: path,
      }
    });

    // Tealium modal event (modal_shown) is fired centrally in ModalService for every ngb-modal-window modal.
  }

  public shopSearchTicket(searchResultAmount: number): void {
    // Ensighten
    this.pushToDataLayer({
      event: 'gaEvent',
      eventSyntax: {
        category: 'klasselotteriet',
        action: 'search_wish_ticket',
        label: searchResultAmount,
      }
    });
    
    // Tealium trackEvent
    trackEvent({ 
      name: 'search_wish_ticket', 
      type: 'link', 
      text: String(searchResultAmount),
    });
  }

  public shopSearchTicketCycleResult(shareType: ShareType): void {
    // Ensighten
    this.pushToDataLayer({
      event: 'gaEvent',
      eventSyntax: {
        category: 'klasselotteriet',
        action: 'refresh_wish_ticket',
        label: shareType.Name,
      }
    });
    
    // Tealium trackEvent
    trackEvent({ 
      name: 'refresh_wish_ticket', 
      type: 'link', 
      text: shareType.Name,
    });
  }

  public basketCtaClick(): void {
    // Ensighten
    this.pushToDataLayer({
      event: 'gaEvent',
      eventSyntax: {
        category: 'klasselotteriet',
        action: 'cta_click',
        label: 'gå til kurv',
      }
    });
    
    // Tealium trackEvent
    trackEvent({ 
      name: 'cta_click', 
      type: 'link', 
      text: 'gå til kurv', 
      action: 'click',
    });
  }

  /**
   * Tracks a click on the "Fortsæt til køb" CTA in the basket that links to payment (/betaling).
   */
  public continueToPaymentClick(): void {
    // Ensighten
    this.pushToDataLayer({
      event: 'gaEvent',
      eventSyntax: {
        category: 'klasselotteriet',
        action: 'cta_click',
        label: 'fortsæt til køb',
      }
    });

    // Tealium trackEvent
    trackEvent({
      name: 'cta_click',
      type: 'link',
      action: 'continue_to_payment',
      text: 'fortsæt til køb',
      eventList: ['click'],
    });
  }

  /**
   * Tracks a click on the "Tilføj flere" CTA in the basket order line list.
   */
  public basketAddMoreClick(): void {
    // Ensighten
    this.pushToDataLayer({
      event: 'gaEvent',
      eventSyntax: {
        category: 'klasselotteriet',
        action: 'cta_click',
        label: 'tilføj flere',
      }
    });

    // Tealium trackEvent
    trackEvent({
      name: 'cta_click',
      type: 'link',
      action: 'add_more_tickets',
      text: 'tilføj flere',
      eventList: ['click'],
    });
  }

  /**
   * Tracks a click on the remove (X) button of a basket order line.
   * @param isPackage true when removing a package (lodpakke), false for a single ticket (lod)
   */
  public basketRemoveItemClick(isPackage: boolean): void {
    const itemType = isPackage ? 'lodpakke' : 'lod';

    // Ensighten
    this.pushToDataLayer({
      event: 'gaEvent',
      eventSyntax: {
        category: 'klasselotteriet',
        action: 'cta_click',
        label: 'fjern ' + itemType,
      }
    });

    // Tealium trackEvent
    trackEvent({
      name: 'cta_click',
      type: 'link',
      action: 'remove_basket_item',
      text: 'fjern ' + itemType,
      category: itemType,
      eventList: ['click'],
    });
  }

  /**
   * Tracks a click on the "Slet lod?"/"Slet lodpakke?" confirmation button in the remove modal.
   * @param isPackage true when confirming removal of a package (lodpakke), false for a single ticket (lod)
   */
  public basketConfirmRemoveItemClick(isPackage: boolean): void {
    const itemType = isPackage ? 'lodpakke' : 'lod';

    // Ensighten
    this.pushToDataLayer({
      event: 'gaEvent',
      eventSyntax: {
        category: 'klasselotteriet',
        action: 'cta_click',
        label: 'slet ' + itemType,
      }
    });

    // Tealium trackEvent — button inside a modal: spec modal event, action 'modal_click'
    trackEvent({
      name: 'confirm_remove_basket_item',
      type: 'link',
      action: 'modal_click',
      text: 'slet ' + itemType,
      category: itemType,
      eventList: ['modal'],
    });
  }

  /**
   * Tracks a modal opening. Per the tracking spec, modal events use `tealium_event: "modal"`
   * (mapped to `event_list: ['modal']`) with the `action` distinguishing shown/closed/clicked.
   * @param modalName a human-readable name (title) for the modal
   */
  public modalOpen(modalName: string): void {
    // Ensighten
    this.pushToDataLayer({
      event: 'gaEvent',
      eventSyntax: {
        category: 'klasselotteriet',
        action: 'modal_open',
        label: modalName,
      }
    });

    // Tealium trackEvent — spec modal event: event_list ['modal'], action 'modal_shown'
    trackEvent({
      name: modalName,
      type: 'link',
      action: 'modal_shown',
      text: modalName,
      eventList: ['modal'],
    });
  }

  /**
   * Tracks a modal closing. Per the tracking spec, modal events use `tealium_event: "modal"`
   * (mapped to `event_list: ['modal']`) with the `action` distinguishing shown/closed/clicked.
   * @param modalName a human-readable name (title) for the modal
   */
  public modalClose(modalName: string): void {
    // Ensighten
    this.pushToDataLayer({
      event: 'gaEvent',
      eventSyntax: {
        category: 'klasselotteriet',
        action: 'modal_close',
        label: modalName,
      }
    });

    // Tealium trackEvent — spec modal event: event_list ['modal'], action 'modal_closed'
    trackEvent({
      name: modalName,
      type: 'link',
      action: 'modal_closed',
      text: modalName,
      eventList: ['modal'],
    });
  }

  /**
   * Tracks selecting a payment method (e.g. Betalingsservice, card) on the /betalingsmetode page.
   * @param methodLabel the label of the selected payment method
   * @param cardType optional friendly card-type label (e.g. "Visa") when a specific credit card is selected
   */
  public checkoutPaymentMethodSelect(methodLabel: string, cardType?: string): void {
    // Ensighten
    this.pushToDataLayer({
      event: 'gaEvent',
      eventSyntax: {
        category: 'klasselotteriet',
        action: 'cta_click',
        label: cardType ? `${methodLabel} - ${cardType}` : methodLabel,
      }
    });

    // Tealium trackEvent
    trackEvent({
      name: 'cta_click',
      type: 'link',
      action: 'select_payment_method',
      text: methodLabel,
      eventList: ['click'],
      // Include the specific credit card type (e.g. "Visa") when a card is selected
      ...(cardType ? { category: cardType } : {}),
    });
  }


  /**
   * Tracks the "Fortsæt" continue button click on the /betalingsmetode page.
   */
  public checkoutContinueClick(): void {
    // Ensighten
    this.pushToDataLayer({
      event: 'gaEvent',
      eventSyntax: {
        category: 'klasselotteriet',
        action: 'cta_click',
        label: 'fortsæt',
      }
    });

    // Tealium trackEvent
    trackEvent({
      name: 'cta_click',
      type: 'link',
      action: 'payment_method_continue',
      text: 'fortsæt',
      eventList: ['click'],
    });
  }

  /**
   * Tracks an error being shown to the user (spec `error` event). Called centrally by
   * `ErrorMessageComponent` so every `app-error-message` is tracked.
   * @param errorName short name/title of the error
   * @param description the user-facing error description
   * @param url optional URL associated with the error (e.g. the retry target)
   */
  public trackError(errorName: string, description?: string, url?: string): void {
    // Ensighten
    this.pushToDataLayer({
      event: 'gaEvent',
      eventSyntax: {
        category: 'klasselotteriet',
        action: 'error',
        label: errorName,
      }
    });

    // Tealium trackEvent — spec error event: event_list ['error']
    trackEvent({
      name: errorName,
      type: 'link',
      action: 'error_shown',
      text: description || '',
      url: url || window.location.href,
      eventList: ['error'],
    });
  }

  /**
   * Tracks a click on a promotion spot (spec `promotion` event, `action: 'promotion_clicked'`).
   * Mirrors the non-Angular `CardView.js` convention: a click on a promotion-spot element uses
   * `event_list: ['click_element']` (so analytics can distinguish it from a normal navigation click)
   * and carries the same `promotion_info` structure as the page-view impression.
   * @param promotion the promotion entries that were clicked
   */
  public promotionClicked(promotion: PromotionProps[]): void {
    // Ensighten
    this.pushToDataLayer({
      event: 'promotionClick',
      ecommerce: {
        promoClick: {
          promotions: promotion,
        }
      }
    });

    // Tealium trackEvent — promotion-spot click, action 'promotion_clicked'
    trackEvent({
      name: 'promotion_click',
      type: 'link',
      action: 'promotion_clicked',
      eventList: ['click_element'],
      promotion: promotion.map((p) => ({ ...p, action: 'promotion_clicked' })),
    });
  }

  // Skal altid komme uanset hvad (pageview)
  public paymentMethodSelection(orderProducts: Object[], membershipState: MembershipState): void {
    // Ensighten
    this.pushToDataLayer({
      event: "checkout",
      ecommerce: {
        checkout: {
          actionField: {
            step: "betalingsmetode",
            customerType: membershipState,
          },
          products: orderProducts
        }
      }
    });

    // Tealium: flow_info is sent on the page_view (see getPageViewFlow), not as a separate flow event.
  }

  // Har valgt bs betaling
  public paymentMethodSelectionBS(orderProducts: Object[], membershipState: MembershipState): void {
    // Ensighten
    this.pushToDataLayer({
      event: "checkout",
      ecommerce: {
        checkout: {
          actionField: {
            step: "bs",
            customerType: membershipState,
          },
          products: orderProducts
        }
      }
    });
    
    // Tealium trackEvent
    trackEvent({ 
      name: 'checkout_payment_bs', 
      type: 'link',
      eventList: ['checkout'],
    });
  }

  // har valgt kortbetaling
  public paymentMethodSelectionCard(orderProducts: Object[], membershipState: MembershipState): void {
    // Ensighten
    this.pushToDataLayer({
      event: "checkout",
      ecommerce: {
        checkout: {
          actionField: {
            step: "card",
            customerType: membershipState,
          },
          products: orderProducts
        }
      }
    });
    
    // Tealium trackEvent
    trackEvent({ 
      name: 'checkout_payment_card', 
      type: 'link',
      eventList: ['checkout'],
    });
  }

  public paymentServiceSubscriptionSelection(orderProducts: Object[], membershipState: MembershipState): void {
    // Ensighten
    this.pushToDataLayer({
      event: "checkout",
      ecommerce: {
        checkout: {
          actionField: {
            step: "bs",
            customerType: membershipState,
          },
          products: orderProducts
        }
      }
    });

    // Tealium: flow_info is sent on the page_view (see getPageViewFlow), not as a separate flow event.
  }

  public paymentServiceCardSubscriptionSelection(orderProducts: Object[], membershipState: MembershipState): void {
    // Ensighten
    this.pushToDataLayer({
      event: "checkout",
      ecommerce: {
        checkout: {
          actionField: {
            step: "card",
            customerType: membershipState,
          },
          products: orderProducts
        }
      }
    });
    
    // Tealium trackEvent
    trackEvent({ 
      name: 'checkout_subscription_card', 
      type: 'link',
    });
  }

  public paymentServiceCreateSubscription(orderProducts: Object[], membershipState: MembershipState): void {
    // Ensighten
    this.pushToDataLayer({
      event: "checkout",
      ecommerce: {
        checkout: {
          actionField: {
            step: "bs_oplysninger",
            customerType: membershipState,
          },
          products: orderProducts
        }
      }
    });

    // Tealium: flow_info is sent on the page_view (see getPageViewFlow), not as a separate flow event.
  }

  public receipt(orderProducts: TrackingProduct[], membershipState: MembershipState): void {
    // Ensighten
    this.pushToDataLayer({
      event: "checkout",
      ecommerce: {
        checkout: {
          actionField: {
            step: "kvittering",
            customerType: membershipState,
          },
          products: orderProducts
        }
      }
    });
    
    // Tealium trackProduct
    trackProduct({ 
      eventList: ['purchase'], 
      products: orderProducts as TrackingProduct[],
    });
  }

    public trackOrderOnPaymentFlowReceipt(order: Order, membershipState: MembershipState): void {
        const classCount = order.NumberOfClasses;
    
        this.priceService
          .getPriceByOrder(order)
          .pipe(
            switchMap((priceData) => {
              const totalPrice = priceData.totalPrice / 100; // + feeValue // Fees do not count as income.
              const variant = this.resolveSubscriptionVariant(classCount);
              const tasks = [];
              const packageTrackingEntities = [];
    
              order.TicketPackageInstances.forEach((ticketPackageInstance) => {
                tasks.push(
                  this.priceService.getPriceByPackage(ticketPackageInstance.TicketPackage).pipe(
                    tap((p) => {
                      packageTrackingEntities.push({
                        name: ticketPackageInstance.TicketPackage.Name,
                        id: ticketPackageInstance.TicketPackage.Name,
                        price:
                          (classCount === 6 ? p.subscriptionSixMonths : p.subscriptionSingleMonth) /
                          100,
                        brand: 'dlo_ddk',
                        category: 'lodpakke', // "Enkeltlodder/Lodpakker/Ønskelodder
                        variant,
                        quantity: 1,
                      });
                    })
                  )
                );
              });
    
              const shareTrackingEntities = [];
              order.Shares.forEach((share) => {
                tasks.push(
                  this.priceService.getPriceByShareType(share.Type).pipe(
                    tap((p) => {
                      shareTrackingEntities.push({
                        name: share.Type.Name,
                        id: share.Type.Name, // SKU Pakker eller lodnr.
                        price:
                          (classCount === 6 ? p.subscriptionSixMonths : p.subscriptionSingleMonth) /
                          100,
                        brand: 'dlo_ddk',
                        category: 'lod', // "Enkeltlodder/Lodpakker/Ønskelodder
                        variant,
                        quantity: 1,
                      });
                    })
                  )
                );
              });
    
              return combineLatest(tasks).pipe(
                tap(() => {
                  const allProducts = shareTrackingEntities.concat(packageTrackingEntities);
                  this.receipt(allProducts, membershipState);
                })
              );
            })
          )
          .pipe(take(1))
          .subscribe();
    }

    public trackOrderOnPaymentFlowSubscription(order: Order, membershipState: MembershipState): void {
        const classCount = order.NumberOfClasses;
    
        this.priceService
          .getPriceByOrder(order)
          .pipe(
            switchMap((priceData) => {
              const totalPrice = priceData.totalPrice / 100; // + feeValue // Fees do not count as income.
              const variant = this.resolveSubscriptionVariant(classCount);
              const tasks = [];
              const packageTrackingEntities = [];
    
              order.TicketPackageInstances.forEach((ticketPackageInstance) => {
                tasks.push(
                  this.priceService.getPriceByPackage(ticketPackageInstance.TicketPackage).pipe(
                    tap((p) => {
                      packageTrackingEntities.push({
                        name: ticketPackageInstance.TicketPackage.Name,
                        id: ticketPackageInstance.TicketPackage.Name,
                        price:
                          (classCount === 6 ? p.subscriptionSixMonths : p.subscriptionSingleMonth) /
                          100,
                        brand: 'dlo_ddk',
                        category: 'lodpakke', // "Enkeltlodder/Lodpakker/Ønskelodder
                        variant,
                        quantity: 1,
                      });
                    })
                  )
                );
              });
    
              const shareTrackingEntities = [];
              order.Shares.forEach((share) => {
                tasks.push(
                  this.priceService.getPriceByShareType(share.Type).pipe(
                    tap((p) => {
                      shareTrackingEntities.push({
                        name: share.Type.Name,
                        id: share.Type.Name, // SKU Pakker eller lodnr.
                        price:
                          (classCount === 6 ? p.subscriptionSixMonths : p.subscriptionSingleMonth) /
                          100,
                        brand: 'dlo_ddk',
                        category: 'lod', // "Enkeltlodder/Lodpakker/Ønskelodder
                        variant,
                        quantity: 1,
                      });
                    })
                  )
                );
              });
    
              return combineLatest(tasks).pipe(
                tap(() => {
                  const allProducts = shareTrackingEntities.concat(packageTrackingEntities);
                  this.paymentServiceCreateSubscription(allProducts, membershipState);
                })
              );
            })
          )
          .pipe(take(1))
          .subscribe();
    }

    public trackOrderOnPaymentFlowSelection(order: Order, membershipState: MembershipState): void {
        const classCount = order.NumberOfClasses;
    
        this.priceService
          .getPriceByOrder(order)
          .pipe(
            switchMap((priceData) => {
              const totalPrice = priceData.totalPrice / 100; // + feeValue // Fees do not count as income.
              const variant = this.resolveSubscriptionVariant(classCount);
              const tasks = [];
              const packageTrackingEntities = [];
    
              order.TicketPackageInstances.forEach((ticketPackageInstance) => {
                tasks.push(
                  this.priceService.getPriceByPackage(ticketPackageInstance.TicketPackage).pipe(
                    tap((p) => {
                      packageTrackingEntities.push({
                        name: ticketPackageInstance.TicketPackage.Name,
                        id: ticketPackageInstance.TicketPackage.Name,
                        price:
                          (classCount === 6 ? p.subscriptionSixMonths : p.subscriptionSingleMonth) /
                          100,
                        brand: 'dlo_ddk',
                        category: 'lodpakke', // "Enkeltlodder/Lodpakker/Ønskelodder
                        variant,
                        quantity: 1,
                      });
                    })
                  )
                );
              });
    
              const shareTrackingEntities = [];
              order.Shares.forEach((share) => {
                tasks.push(
                  this.priceService.getPriceByShareType(share.Type).pipe(
                    tap((p) => {
                      shareTrackingEntities.push({
                        name: share.Type.Name,
                        id: share.Type.Name, // SKU Pakker eller lodnr.
                        price:
                          (classCount === 6 ? p.subscriptionSixMonths : p.subscriptionSingleMonth) /
                          100,
                        brand: 'dlo_ddk',
                        category: 'lod', // "Enkeltlodder/Lodpakker/Ønskelodder
                        variant,
                        quantity: 1,
                      });
                    })
                  )
                );
              });
    
              return combineLatest(tasks).pipe(
                tap(() => {
                  const allProducts = shareTrackingEntities.concat(packageTrackingEntities);
                  this.paymentServiceSubscriptionSelection(allProducts, membershipState);
                })
              );
            })
          )
          .pipe(take(1))
          .subscribe();
    }

    public trackOrderOnPaymentFlowPayMethod(order: Order, membershipState: MembershipState): void {
        const classCount = order.NumberOfClasses;
    
        this.priceService
          .getPriceByOrder(order)
          .pipe(
            switchMap((priceData) => {
              const totalPrice = priceData.totalPrice / 100; // + feeValue // Fees do not count as income.
              const variant = this.resolveSubscriptionVariant(classCount);
              const tasks = [];
              const packageTrackingEntities = [];
    
              order.TicketPackageInstances.forEach((ticketPackageInstance) => {
                tasks.push(
                  this.priceService.getPriceByPackage(ticketPackageInstance.TicketPackage).pipe(
                    tap((p) => {
                      packageTrackingEntities.push({
                        name: ticketPackageInstance.TicketPackage.Name,
                        id: ticketPackageInstance.TicketPackage.Name,
                        price:
                          (classCount === 6 ? p.subscriptionSixMonths : p.subscriptionSingleMonth) /
                          100,
                        brand: 'dlo_ddk',
                        category: 'lodpakke', // "Enkeltlodder/Lodpakker/Ønskelodder
                        variant,
                        quantity: 1,
                      });
                    })
                  )
                );
              });
    
              const shareTrackingEntities = [];
              order.Shares.forEach((share) => {
                tasks.push(
                  this.priceService.getPriceByShareType(share.Type).pipe(
                    tap((p) => {
                      shareTrackingEntities.push({
                        name: share.Type.Name,
                        id: share.Type.Name, // SKU Pakker eller lodnr.
                        price:
                          (classCount === 6 ? p.subscriptionSixMonths : p.subscriptionSingleMonth) /
                          100,
                        brand: 'dlo_ddk',
                        category: 'lod', // "Enkeltlodder/Lodpakker/Ønskelodder
                        variant,
                        quantity: 1,
                      });
                    })
                  )
                );
              });
    
              return combineLatest(tasks).pipe(
                tap(() => {
                  const allProducts = shareTrackingEntities.concat(packageTrackingEntities);
                  this.paymentMethodSelection(allProducts, membershipState);
                })
              );
            })
          )
          .pipe(take(1))
          .subscribe();
    }

    public trackOrderOnPaymentFlowPayMethodBS(order: Order, membershipState: MembershipState): void {
        const classCount = order.NumberOfClasses;
    
        this.priceService
          .getPriceByOrder(order)
          .pipe(
            switchMap((priceData) => {
              const totalPrice = priceData.totalPrice / 100; // + feeValue // Fees do not count as income.
              const variant = this.resolveSubscriptionVariant(classCount);
              const tasks = [];
              const packageTrackingEntities = [];
    
              order.TicketPackageInstances.forEach((ticketPackageInstance) => {
                tasks.push(
                  this.priceService.getPriceByPackage(ticketPackageInstance.TicketPackage).pipe(
                    tap((p) => {
                      packageTrackingEntities.push({
                        name: ticketPackageInstance.TicketPackage.Name,
                        id: ticketPackageInstance.TicketPackage.Name,
                        price:
                          (classCount === 6 ? p.subscriptionSixMonths : p.subscriptionSingleMonth) /
                          100,
                        brand: 'dlo_ddk',
                        category: 'lodpakke', // "Enkeltlodder/Lodpakker/Ønskelodder
                        variant,
                        quantity: 1,
                      });
                    })
                  )
                );
              });
    
              const shareTrackingEntities = [];
              order.Shares.forEach((share) => {
                tasks.push(
                  this.priceService.getPriceByShareType(share.Type).pipe(
                    tap((p) => {
                      shareTrackingEntities.push({
                        name: share.Type.Name,
                        id: share.Type.Name, // SKU Pakker eller lodnr.
                        price:
                          (classCount === 6 ? p.subscriptionSixMonths : p.subscriptionSingleMonth) /
                          100,
                        brand: 'dlo_ddk',
                        category: 'lod', // "Enkeltlodder/Lodpakker/Ønskelodder
                        variant,
                        quantity: 1,
                      });
                    })
                  )
                );
              });
    
              return combineLatest(tasks).pipe(
                tap(() => {
                  const allProducts = shareTrackingEntities.concat(packageTrackingEntities);
                  this.paymentMethodSelectionBS(allProducts, membershipState);
                })
              );
            })
          )
          .pipe(take(1))
          .subscribe();
    }

    public trackOrderOnPaymentFlowPayMethodCard(order: Order, membershipState: MembershipState): void {
        const classCount = order.NumberOfClasses;
    
        this.priceService
          .getPriceByOrder(order)
          .pipe(
            switchMap((priceData) => {
              const totalPrice = priceData.totalPrice / 100; // + feeValue // Fees do not count as income.
              const variant = this.resolveSubscriptionVariant(classCount);
              const tasks = [];
              const packageTrackingEntities = [];
    
              order.TicketPackageInstances.forEach((ticketPackageInstance) => {
                tasks.push(
                  this.priceService.getPriceByPackage(ticketPackageInstance.TicketPackage).pipe(
                    tap((p) => {
                      packageTrackingEntities.push({
                        name: ticketPackageInstance.TicketPackage.Name,
                        id: ticketPackageInstance.TicketPackage.Name,
                        price:
                          (classCount === 6 ? p.subscriptionSixMonths : p.subscriptionSingleMonth) /
                          100,
                        brand: 'dlo_ddk',
                        category: 'lodpakke', // "Enkeltlodder/Lodpakker/Ønskelodder
                        variant,
                        quantity: 1,
                      });
                    })
                  )
                );
              });
    
              const shareTrackingEntities = [];
              order.Shares.forEach((share) => {
                tasks.push(
                  this.priceService.getPriceByShareType(share.Type).pipe(
                    tap((p) => {
                      shareTrackingEntities.push({
                        name: share.Type.Name,
                        id: share.Type.Name, // SKU Pakker eller lodnr.
                        price:
                          (classCount === 6 ? p.subscriptionSixMonths : p.subscriptionSingleMonth) /
                          100,
                        brand: 'dlo_ddk',
                        category: 'lod', // "Enkeltlodder/Lodpakker/Ønskelodder
                        variant,
                        quantity: 1,
                      });
                    })
                  )
                );
              });
    
              return combineLatest(tasks).pipe(
                tap(() => {
                  const allProducts = shareTrackingEntities.concat(packageTrackingEntities);
                  this.paymentMethodSelectionCard(allProducts, membershipState);
                })
              );
            })
          )
          .pipe(take(1))
          .subscribe();
    };

    /**
     * Builds the full product list from the current order and sends a basket view (view_cart)
     * trackProduct event. Intended for the /lodder-til-salg/kurv page.
     */
    public trackOrderOnBasketView(order: Order): void {
        if (order == null || (order.Shares.length === 0 && order.TicketPackageInstances.length === 0)) {
            return;
        }

        const classCount = order.NumberOfClasses;

        this.priceService
          .getPriceByOrder(order)
          .pipe(
            switchMap((priceData) => {
              const cartTotalPrice = priceData.totalPrice / 100;
              const tasks = [];
              const products: TrackingProduct[] = [];

              order.TicketPackageInstances.forEach((ticketPackageInstance) => {
                tasks.push(
                  this.priceService.getPriceByPackage(ticketPackageInstance.TicketPackage).pipe(
                    tap((p) => {
                      products.push({
                        id: ticketPackageInstance.TicketPackage.Id,
                        name: 'Klasselotteriet lodpakker ' + ticketPackageInstance.TicketPackage.Name,
                        quantity: 1,
                        rows: ticketPackageInstance.TicketPackage.TicketPackageContents.reduce((sum, c) => sum + c.NumberOfTickets, 0),
                        type: 'Lodpakker',
                        unit_price:
                          (classCount === 6 ? p.subscriptionSixMonths : p.subscriptionSingleMonth) / 100,
                        variant: ticketPackageInstance.TicketPackage.Name,
                      } as TrackingProduct);
                    })
                  )
                );
              });

              order.Shares.forEach((share) => {
                tasks.push(
                  this.priceService.getPriceByShareType(share.Type).pipe(
                    tap((p) => {
                      products.push({
                        id: share.Type.Id,
                        name: 'Klasselotteriet enkeltlodder ' + share.Type.Name,
                        quantity: 1,
                        rows: 1,
                        type: 'Enkeltlodder',
                        unit_price:
                          (classCount === 6 ? p.subscriptionSixMonths : p.subscriptionSingleMonth) / 100,
                        variant: share.Type.Name,
                      } as TrackingProduct);
                    })
                  )
                );
              });

              return combineLatest(tasks).pipe(
                tap(() => {
                  const cartTotalItems = order.Shares.length + order.TicketPackageInstances.length;
                  const packageRows = order.TicketPackageInstances.reduce((sum, tpi) => sum + tpi.Shares.length, 0);
                  const cartTotalRows = order.Shares.length + packageRows;

                  this.basketView(products, cartTotalItems, cartTotalRows, cartTotalPrice);
                })
              );
            })
          )
          .pipe(take(1))
          .subscribe();
    }

    /**
     * Builds the full product list from the order and sends a payment method page view (checkout)
     * trackProduct event. Intended for the /betalingsmetode page.
     */
    public trackOrderOnPaymentMethodView(order: Order): void {
        if (order == null || (order.Shares.length === 0 && order.TicketPackageInstances.length === 0)) {
            return;
        }

        const classCount = order.NumberOfClasses;

        this.priceService
          .getPriceByOrder(order)
          .pipe(
            switchMap((priceData) => {
              const cartTotalPrice = priceData.totalPrice / 100;
              const tasks = [];
              const products: TrackingProduct[] = [];

              order.TicketPackageInstances.forEach((ticketPackageInstance) => {
                tasks.push(
                  this.priceService.getPriceByPackage(ticketPackageInstance.TicketPackage).pipe(
                    tap((p) => {
                      products.push({
                        id: ticketPackageInstance.TicketPackage.Id,
                        name: 'Klasselotteriet lodpakker ' + ticketPackageInstance.TicketPackage.Name,
                        quantity: 1,
                        rows: ticketPackageInstance.TicketPackage.TicketPackageContents.reduce((sum, c) => sum + c.NumberOfTickets, 0),
                        type: 'Lodpakker',
                        unit_price:
                          (classCount === 6 ? p.subscriptionSixMonths : p.subscriptionSingleMonth) / 100,
                        variant: ticketPackageInstance.TicketPackage.Name,
                      } as TrackingProduct);
                    })
                  )
                );
              });

              order.Shares.forEach((share) => {
                tasks.push(
                  this.priceService.getPriceByShareType(share.Type).pipe(
                    tap((p) => {
                      products.push({
                        id: share.Type.Id,
                        name: 'Klasselotteriet enkeltlodder ' + share.Type.Name,
                        quantity: 1,
                        rows: 1,
                        type: 'Enkeltlodder',
                        unit_price:
                          (classCount === 6 ? p.subscriptionSixMonths : p.subscriptionSingleMonth) / 100,
                        variant: share.Type.Name,
                      } as TrackingProduct);
                    })
                  )
                );
              });

              return combineLatest(tasks).pipe(
                tap(() => {
                  const cartTotalItems = order.Shares.length + order.TicketPackageInstances.length;
                  const packageRows = order.TicketPackageInstances.reduce((sum, tpi) => sum + tpi.Shares.length, 0);
                  const cartTotalRows = order.Shares.length + packageRows;

                  this.paymentMethodView(products, cartTotalItems, cartTotalRows, cartTotalPrice);
                })
              );
            })
          )
          .pipe(take(1))
          .subscribe();
    }

  public purchaseComplete(orderId: number, totalPrice: number, membershipState: MembershipState, productArray: TrackingProduct[], cartTotalItems?: number, cartTotalRows?: number, paymentMethod?: string, promotion?: PromotionProps[]): void {
    // Ensighten
    this.pushToDataLayer({
      event: "purchaseComplete",
      ecommerce: {
        purchase: {
          actionField: {
            id: orderId,
            revenue: totalPrice,
            customerType: membershipState,
          },
          products: productArray
        }
      }
    });
    
    // Tealium trackProduct
    trackProduct({
      eventList: ['purchase_complete'],
      products: productArray as TrackingProduct[], 
      transaction_id: String(orderId), 
      cart_total: String(totalPrice),
      cart_total_items: cartTotalItems !== undefined ? String(cartTotalItems) : undefined,
      cart_total_rows: cartTotalRows !== undefined ? String(cartTotalRows) : undefined,
      // "Betalingskort" / "Mobilepay" / "Betalingsservice"
      ...(paymentMethod ? { payment_method: paymentMethod } : {}),
      // Promotion spots shown on the receipt (e.g. the gap-funding upsell) — matched on this event.
      ...(promotion && promotion.length > 0
        ? { promotion: promotion.map((p) => ({ ...p, action: 'promotion_shown' })) }
        : {}),
    });
  };
  
  public gapfundComplete(orderId: number, totalPrice: number, membershipState: MembershipState, productArray: object[], paymentMethod?: string): void {
    // Ensighten
    this.pushToDataLayer({
      event: "gapfundComplete",
      ecommerce: {
        purchase: {
          actionField: {
            id: orderId,
            revenue: totalPrice,
            customerType: membershipState,
          },
          products: productArray
        }
      }
    });
    
    // Tealium trackProduct
    trackProduct({ 
      eventList: ['gapfund_complete'], 
      products: productArray as any[], 
      transaction_id: String(orderId), 
      cart_total: String(totalPrice),
      // "Betalingskort" / "Mobilepay"
      ...(paymentMethod ? { payment_method: paymentMethod } : {}),
    });
  };
};
